import { FC, useEffect, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { Dropdown, Input, Loading, Section, Table } from '../../../../components';
import { Option } from '../../../../components/dropdown/Dropdown';
import { TAnuncioTabela, getOffersByDealerId } from '../../../../services';
import { useToastStore, useUserStore } from '../../../../stores';
import { TagStatus, moneyFormatter } from '../../../../utils';
import {
	Form,
	FullLine,
	HalfLine,
	NoData,
	Separator,
	TableWrapper,
	TitleContainer,
} from '../../Reports.styles';

interface IGraphLineData {
	label: string;
	qtde: number;
}

const SellerReport: FC = () => {
	const { user } = useUserStore();
	const { setToast } = useToastStore();
	const [loading, setLoading] = useState(false);
	const [offers, setOffers] = useState<TAnuncioTabela[]>([]);
	const [filteredOffers, setFilteredOffers] = useState<TAnuncioTabela[]>([]);
	const [startDate, setStartDate] = useState('');
	const [endDate, setEndDate] = useState('');
	const [graphLineData, setGraphLineData] = useState<IGraphLineData[]>([]);

	const dealerOptions = useMemo(() => {
		const options =
			user?.concessionarias?.map((item) => ({ value: item.id, label: item.fantasia || '' })) || [];
		return [{ value: 0, label: 'Todas Concessionárias' }, ...options];
	}, [user?.concessionarias]);

	const [selectedOption, setSelectedOption] = useState<Option>(
		dealerOptions.length === 1 ? dealerOptions[0] : { value: 0, label: 'Todas Concessionárias' }
	);

	const handleSelectOption = (option: Option) => {
		setSelectedOption(option);
	};

	useEffect(() => {
		const fetchData = async () => {
			if (user?.id && startDate && endDate && endDate >= startDate && offers.length === 0) {
				setLoading(true);
				const data = await getOffersByDealerId({ id: user.id });

				if (!data) {
					setToast({ variant: 'error', message: 'Erro ao buscar dados!' });
					return;
				}

				const sortedOffers = data.filter((offer) => offer.tagStatus === TagStatus.Sold);

				setOffers(sortedOffers);
			}
		};

		fetchData().finally(() => setTimeout(() => setLoading(false), 500));
	}, [user?.id, startDate, endDate, offers, setToast]);

	useEffect(() => {
		if (!startDate || !endDate) return;

		if (new Date(startDate).getTime() > new Date(endDate).getTime()) {
			setEndDate(startDate);
		}
	}, [startDate, endDate]);

	useEffect(() => {
		if (startDate && endDate && endDate >= startDate && offers?.length) {
			setLoading(true);

			let filtered = offers.filter(
				(offer) =>
					offer.dataVenda &&
					new Date(offer.dataVenda).getTime() >= new Date(startDate).getTime() &&
					new Date(offer.dataVenda).getTime() <= new Date(endDate).getTime()
			);

			if (selectedOption.value !== 0) {
				filtered = filtered.filter(
					(offer) => offer.idConcessionaria === selectedOption.value.toString()
				);
			}

			const salesCountByMonthYear: IGraphLineData[] = [];

			filtered?.forEach((offer) => {
				const dateVenda = new Date(offer.dataVenda!);
				const year = dateVenda.getFullYear();
				const month = dateVenda.getMonth() + 1;
				const label = `${month}/${year}`;
				const existingEntryIndex = salesCountByMonthYear.findIndex(
					(entry) => entry.label === label
				);

				if (existingEntryIndex !== -1) {
					salesCountByMonthYear[existingEntryIndex].qtde++;
				} else {
					salesCountByMonthYear.push({ label, qtde: 1 });
				}
			});

			if (salesCountByMonthYear.length > 0) {
				setGraphLineData(salesCountByMonthYear);
			} else {
				setGraphLineData([]);
			}

			setFilteredOffers(filtered);
			setTimeout(() => setLoading(false), 500);
			return;
		}

		setFilteredOffers([]);
		setGraphLineData([]);
	}, [startDate, endDate, selectedOption, offers]);

	const tableColumns = [
		{
			id: 'plate',
			label: 'Placa',
		},
		{
			id: 'vehicle',
			label: 'Veículo',
		},
		{
			id: 'buyer',
			label: 'Comprador',
		},
		{
			id: 'offerDate',
			label: 'Data do Cadastro',
			width: '100px',
		},
		{
			id: 'offerSellerDate',
			label: 'Data da Venda',
			width: '100px',
		},
		{
			id: 'offerValue',
			label: 'Valor de Venda',
			width: '100px',
		},
	];

	const rowMapper = (item: TAnuncioTabela) => ({
		plate: { value: item?.placa, rawValue: item?.placa },
		vehicle: {
			value: `${item?.veiculoMarca} ${item?.veiculoModelo} ${item?.anoFabricacao}/${item?.anoModelo}`,
			rawValue: `${item?.veiculoMarca} ${item?.veiculoModelo} ${item?.anoFabricacao}/${item?.anoModelo}`,
		},
		buyer: { value: item.revendaCompradora, rawValue: item.revendaCompradora },
		offerValue: {
			value: item?.valorVenda ? moneyFormatter(item.valorVenda) : 0,
			rawValue: item?.valorVenda ? item.valorVenda : 0,
		},
		offerDate: {
			value: new Date(item.dataCadastro).toLocaleDateString('pt-BR'),
			rawValue: new Date(item.dataCadastro).getTime(),
		},
		offerSellerDate: {
			value: item.dataVenda ? new Date(item.dataVenda).toLocaleDateString('pt-BR') : '',
			rawValue: item.dataVenda ? new Date(item.dataVenda).getTime() : '',
		},
	});

	return (
		<>
			<Section>
				<TitleContainer>Relatório de Vendas</TitleContainer>
				<Separator />
				<Form>
					<FullLine>
						<Dropdown
							options={dealerOptions}
							selected={selectedOption}
							onSelect={handleSelectOption}
						/>
					</FullLine>
					<HalfLine>
						<Input
							label='Data de Venda Inicial'
							variant='text'
							value={startDate}
							onChange={(e) => {
								setStartDate(e.target.value);
							}}
							type='date'
						/>
					</HalfLine>
					<HalfLine>
						<Input
							label='Data de Venda Final'
							variant='text'
							value={endDate}
							onChange={(e) => {
								setEndDate(e.target.value);
							}}
							type='date'
							min={startDate}
						/>
					</HalfLine>
				</Form>
				{loading && (
					<NoData>
						<Loading />
					</NoData>
				)}
				{!loading && graphLineData.length > 0 && (
					<FullLine>
						<Line
							data={{
								labels: graphLineData.map((item) => item.label),
								datasets: [
									{
										label: 'Vendas por período (últimos 12 meses)',
										backgroundColor: 'rgba(255, 102, 0)',
										borderColor: 'rgba(255, 102, 0)',
										data: graphLineData.map((item) => item.qtde),
									},
								],
							}}
						/>
					</FullLine>
				)}
				{!loading && filteredOffers.length > 0 && (
					<TableWrapper>
						<Table
							title='Relatório de Vendas'
							columns={tableColumns}
							rows={filteredOffers.map(rowMapper)}
							exportToPDF
							exportToCSV
						/>
					</TableWrapper>
				)}
				{!loading && startDate && endDate && filteredOffers.length === 0 && (
					<NoData>Nenhum registro encontrado</NoData>
				)}
			</Section>
		</>
	);
};

export default SellerReport;
